import {graphqlRequestHandler} from 'constants/Api';
import {IGenericResponse, IGenericType, IUser} from 'constants/interfaces';
import {EActiveState, ISimpleProduceItem, TFarmField} from '../models';
import {lshGETAUTH} from 'services/LocalStorageService';
const getAuthHeader = async () => {
  const token = await lshGETAUTH()
    .then((data) => data)
    .catch(() => '');
  return {Authorization: token};
};

/**
 * Fetch client data to show in the client applications and all clients tables
 * @param {string} searchString
 * @param {number} limit
 * @param {number} offset
 * @return {Promise<Array<IUser>>}
 */
export async function apiGetClientApplications(
  searchString: string,
  limit?: number,
  offset?: number,
): Promise<Array<IUser>> {
  const variables = {
    searchString,
    filters: {
      active: EActiveState.APPROVED,
    },
    limit,
    offset,
  };
  const query = `
      query variables (
        $searchString: String,
        $limit: Int,
        $offset: Int
      ) {
        clients(
            searchString: $searchString,
            limit: $limit,
            offset: $offset
        ) {
          id
          userId
            updateDate
            company {
                name
            companyType {
              type
            }
            province
            }
          name
          surname
          email
          phone
          active
        }
      }
    `;
  const headers = await getAuthHeader();
  const data = await graphqlRequestHandler(
    'clients',
    query,
    variables,
    headers,
  );
  return data as Array<IUser>;
}

/**
 * Api to search through product list
 * @return {Promise}
 * @param {string} searchText
 * @param {number} limit
 */
export async function apiSEARCHPRODUCE(
  searchText: string,
  limit: number,
): Promise<Array<ISimpleProduceItem>> {
  return new Promise((resolve, reject) => {
    const query = `
              query variables($searchText: String!, $limit: Int){
                searchProduce(searchText:$searchText, limit:$limit){
                        id
                        name
                        packaging{
                            id
                            packaging
                        }
                        sizes{
                            id
                            name
                        }
                        variety{
                            id
                            name
                        }
                        grading{
                            id
                            name
                            type
                        }
                }
              }`;
    const variables = {searchText, limit};
    graphqlRequestHandler('searchProduce', query, variables)
      .then((info) => {
        const data = info as Array<ISimpleProduceItem>;
        if (data) {
          resolve(data);
        } else {
          const error = new Error('no data');
          reject(error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Api to search through product list
 * @return {Promise}
 */
export async function apiGETFEES(): Promise<
  Array<{id: string; feeName: string; feePercentage: number}>
> {
  return new Promise(async (resolve, reject) => {
    const query = `
                query{
                  getPlatformFees{
                    id
                    feeName
                    feePercentage
                  }
                }`;
    const variables = {};
    const headers = await getAuthHeader();
    graphqlRequestHandler('getPlatformFees', query, variables, headers)
      .then((info) => {
        const data = info as Array<{
          id: string;
          feeName: string;
          feePercentage: number;
        }>;
        if (data) {
          resolve(data);
        } else {
          const error = new Error('no data');
          reject(error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Api to search through product list
 * @return {Promise}
 */
export async function apiGETPURCHASEORDERDELIVERYTERMS(): Promise<
  Array<IGenericType>
> {
  return new Promise(async (resolve, reject) => {
    const query = `
                query{
                  getPurchaseOrderDeliveryTermsTypes{
                    id
                    name
                  }
                }`;
    const variables = {};
    const headers = await getAuthHeader();
    graphqlRequestHandler(
      'getPurchaseOrderDeliveryTermsTypes',
      query,
      variables,
      headers,
    )
      .then((info) => {
        const data = info as Array<IGenericType>;
        if (data) {
          resolve(data);
        } else {
          const error = new Error('no data');
          reject(error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Api to get purchase order delivery preferences
 * @return {Promise}
 */
export async function apiGETPURCHASEORDERDELIVERYPREFERENCES(): Promise<
  Array<IGenericType>
> {
  return new Promise(async (resolve, reject) => {
    const query = `
              query{
                getPurchaseOrderDeliveryPreferences{
                  id
                  name
                }
              }`;
    const variables = {};
    const headers = await getAuthHeader();
    graphqlRequestHandler(
      'getPurchaseOrderDeliveryPreferences',
      query,
      variables,
      headers,
    )
      .then((info) => {
        const data = info as Array<IGenericType>;
        if (data) {
          resolve(data);
        } else {
          const error = new Error('no data');
          reject(error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Api to get farm fields
 * @param {id} farmId
 * @return {Promise}
 */
export async function apiGETFARMFIELDS(
  farmId: string,
): Promise<Array<TFarmField>> {
  return new Promise(async (resolve, reject) => {
    const query = `
                query variables($farmId: ID!){
                  getFarmFields(farmId: $farmId){
                    id
                    info{
                      fieldName
                    }
                  }
                }`;
    const variables = {farmId};
    const headers = await getAuthHeader();
    graphqlRequestHandler('getFarmFields', query, variables, headers)
      .then((info) => {
        const data = info as Array<TFarmField>;
        if (data) {
          resolve(data);
        } else {
          const error = new Error('no data');
          reject(error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Api to get farm produce contract types
 * @return {Promise}
 */
export async function apiGETFARMPRODUCECONTRACTTYPES(): Promise<
  Array<IGenericType>
> {
  return new Promise(async (resolve, reject) => {
    const query = `
              query{
                getFarmProduceContractTypes{
                  id
                  name
                }
              }`;
    const variables = {};
    const headers = await getAuthHeader();
    graphqlRequestHandler(
      'getFarmProduceContractTypes',
      query,
      variables,
      headers,
    )
      .then((info) => {
        const data = info as Array<IGenericType>;
        if (data) {
          resolve(data);
        } else {
          const error = new Error('no data');
          reject(error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

type ID = number | string;

/**
 * Api to create farm produce contract
 * @param {id} input
 * @return {Promise}
 */
export async function apiCREATEFARMPRODUCECONTRACT(input: {
  farmerId: ID;
  produceId: ID;
  varietyId: ID;
  buyerId: ID;
  fieldType: ID;
  expectedPrice: number;
  expectedPriceUnit: String;
  contractTypeId: ID;
  deliveryPreferenceId: ID;
  deliveryTermsId: ID;
  hectaresPlanted: number;
  estimatedHarvestQuantity: number;
  plantedDate: String;
  estimatedSupplyDate: String;
  fees?: Array<{
    id: ID;
    amount: number;
  }>;
  fields?: Array<ID>;
}): Promise<IGenericResponse> {
  return new Promise(async (resolve, reject) => {
    const query = `
      mutation variables($input: ProduceContractInput!){
        createProduceContract(input: $input){
          success
          message
        }
      }`;
    const variables = {input};
    const headers = await getAuthHeader();
    graphqlRequestHandler('createProduceContract', query, variables, headers)
      .then((info) => {
        const data = info as IGenericResponse;
        if (data) {
          resolve(data);
        } else {
          const error = new Error('no data');
          reject(error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}
